import React, { useEffect, useState } from "react";
import { Alert, Button, Tag, App } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable from "../../../components/DataTable";
import ActivityIndicator from "../../../components/ActivityIndicator";
import {
  GameQuizEntity,
  GameQuizUsage,
} from "../../../services/gameQuiz/gameQuiz.types";
import {
  useGetGameQuizzesPaginatedQuery,
  useDeleteGameQuizMutation,
  useLazyGetGameQuizUsageQuery,
} from "../../../store/api/gameQuizzesApi";
import DeleteGameQuizModal from "./DeleteGameQuizModal";
import TableSearchBar from "../../../components/TableSearchBar";
import useDebounce from "../../../hooks/useDebounce";
import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";
import PageHeader from "../../../components/PageHeader";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";

const DEFAULT_PAGE_SIZE = 10;

const GameQuizzes: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();

  // The URL is the source of truth for page, pageSize, search and id: the
  // list is deep-linkable and survives refreshes. The inputs keep their own
  // local state and sync into the URL after the debounce below.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const searchParam = searchParams.get("search") ?? "";
  const idParam = searchParams.get("id") ?? "";
  // SCRUM-975: the id filter only reaches the backend when it is a whole
  // number; a hand-edited URL like ?id=abc must not break the request.
  const idFilter = /^\d+$/.test(idParam) ? Number(idParam) : undefined;

  const [searchTitle, setSearchTitle] = useState(searchParam);
  const [searchId, setSearchId] = useState(idParam);
  const debouncedSearchTitle = useDebounce(searchTitle, 500);
  const debouncedSearchId = useDebounce(searchId, 500);

  // Debounced inputs → URL. Changing a filter always resets to page 1.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentSearch = prev.get("search") ?? "";
        const currentId = prev.get("id") ?? "";
        if (
          currentSearch === debouncedSearchTitle &&
          currentId === debouncedSearchId
        ) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        if (debouncedSearchTitle) next.set("search", debouncedSearchTitle);
        else next.delete("search");
        if (debouncedSearchId) next.set("id", debouncedSearchId);
        else next.delete("id");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearchTitle, debouncedSearchId, setSearchParams]);

  // `data` (instead of `currentData`) keeps the previous page visible while
  // the next one is fetching, so the table doesn't flicker to empty. RTK
  // Query fires exactly one request per distinct query arg, which also fixes
  // the old double fetch on mount (refetch() effect + debounce storm).
  const { data, isFetching, isError, refetch } =
    useGetGameQuizzesPaginatedQuery({
      page,
      pageSize,
      search: searchParam || undefined,
      id: idFilter,
    });
  const [deleteGameQuiz, { isLoading: isDeleting }] =
    useDeleteGameQuizMutation();
  const [fetchUsage, { isFetching: isFetchingUsage }] =
    useLazyGetGameQuizUsageQuery();

  // Deletion is a two-step flow: resolve what depends on the quiz, show it, and
  // only then delete with an explicit acknowledgement. See DeleteGameQuizModal.
  const [pendingDelete, setPendingDelete] = useState<GameQuizEntity | null>(
    null,
  );
  const [pendingUsage, setPendingUsage] = useState<GameQuizUsage | null>(null);

  const gameQuizzes = data?.data ?? [];
  const pagination = data?.pagination ?? { page, pageSize, total: 0 };
  const isInitialLoad = useIsInitialLoad(isFetching, gameQuizzes);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      next.set("pageSize", String(newPageSize));
      return next;
    });
  };

  // Deleting the last item of the last page must not leave an empty table.
  useClampPageToTotal({
    total: pagination.total,
    pageSize,
    page,
    onPageChange: (lastPage) => handlePageChange(lastPage, pageSize),
  });

  const handleEdit = (record: GameQuizEntity) => {
    navigate(`/game-quizzes/${record.id}`);
  };

  // Step 1 — open the impact dialog and resolve what depends on this quiz. No
  // deletion happens here.
  const handleDelete = async (record: GameQuizEntity) => {
    setPendingDelete(record);
    setPendingUsage(null);
    try {
      setPendingUsage(await fetchUsage(record.id).unwrap());
    } catch (error: any) {
      setPendingDelete(null);
      notification["error"]({
        message: "Error",
        description:
          error?.data?.message ||
          "No se pudieron revisar las conexiones del quiz. No se eliminó nada.",
        placement: "top",
        duration: 4,
      });
    }
  };

  // Step 2 — the admin saw the impact and accepted it.
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      const result = await deleteGameQuiz({
        id: pendingDelete.id,
        // Always confirmed: this path is only reachable from the impact dialog.
        confirm: true,
      }).unwrap();

      const cleaned = [
        result.deletedResourceIds.length &&
          `${result.deletedResourceIds.length} recurso(s) eliminado(s)`,
        result.deletedHomeworkIds.length &&
          `${result.deletedHomeworkIds.length} tarea(s) quitada(s)`,
        result.unlinkedResourceIds.length &&
          `${result.unlinkedResourceIds.length} recurso(s) desvinculado(s)`,
      ].filter(Boolean) as string[];

      notification["success"]({
        message: "Éxito",
        description: cleaned.length
          ? `Quiz eliminado. ${cleaned.join(", ")}.`
          : "Quiz eliminado exitosamente.",
        placement: "top",
        duration: 4,
      });
      setPendingDelete(null);
      setPendingUsage(null);
    } catch (error: any) {
      notification["error"]({
        message: "Error",
        description: error?.data?.message || "El quiz no pudo ser eliminado.",
        placement: "top",
        duration: 4,
      });
    }
  };

  const handleCancelDelete = () => {
    setPendingDelete(null);
    setPendingUsage(null);
  };

  return (
    <div>
      <PageHeader
        title="Quizzes Emmi"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por título",
                value: searchTitle,
                onChange: setSearchTitle,
                width: 240,
              },
              {
                placeholder: "Buscar por ID",
                value: searchId,
                // Digits only: the id is an integer, and a stray letter would
                // silently disable the filter instead of narrowing it.
                onChange: (value) => setSearchId(value.replace(/\D/g, "")),
                width: 140,
              },
            ]}
          >
            <Button
              type="primary"
              onClick={() => navigate("/game-quizzes/new")}
            >
              Nueva quiz
            </Button>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar los quizzes."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={gameQuizzes}
          columns={[
            { title: "ID", dataIndex: "id", key: "id", width: 80 },
            {
              title: "Título",
              dataIndex: "title",
              key: "title",
              width: 280,
            },
            {
              title: "Preguntas",
              dataIndex: "games",
              key: "games",
              width: 110,
              render: (games: unknown[] | null) => (games ? games.length : 0),
            },
            {
              title: "Estado",
              dataIndex: "published",
              key: "published",
              width: 120,
              render: (published: boolean) =>
                published ? (
                  <Tag color="green">Publicado</Tag>
                ) : (
                  <Tag>Borrador</Tag>
                ),
            },
          ]}
          onEdit={handleEdit}
          onDelete={handleDelete}
          // The generic Popconfirm is skipped on purpose: confirming here would
          // hide the impact. DeleteGameQuizModal is the confirmation.
          skipDeleteConfirm
          loading={isFetching}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
        />
      )}

      <DeleteGameQuizModal
        open={pendingDelete !== null}
        usage={pendingUsage}
        loading={isFetchingUsage}
        deleting={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default GameQuizzes;
